/**
 * Home container component responsible for acting as a container for the homepage.
 * @namespace HomeContainer
 * @memberOf Home
 */
import React, { useEffect, useState } from "react"
import { useUserStore } from "stores/userStore"

import ProjectDisplay from "features/Projects/components/ProjectDisplay.component"

import {
  useProjectsQuery,
  useArchivedProjectsQuery,
  useProjectFoldersQuery,
} from "features/Projects/api/useProjectsQuery"
import {
  useArchiveProjectMutation,
  useUnarchiveProjectMutation,
} from "features/Projects/api/useProjectsMutations"
import { useUser } from "hooks/api/useUser"
import { PROJECT_FILTER } from "types/project.types"

const HomeContainer: React.FC = () => {
  const { getUserData } = useUser()
  const user = useUserStore((state) => state.user)

  const [filter, setFilter] = useState<PROJECT_FILTER>(PROJECT_FILTER.OWNED)
  // The archived list is only fetched once the user opens the archived view
  // (ProjectDisplay calls getArchivedProjects on toggle).
  const [archivedRequested, setArchivedRequested] = useState<boolean>(false)

  const userReady = Boolean(user.username && user.unit)

  useEffect(() => {
    if (!user.username || !user.unit) {
      getUserData()
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const projectsQuery = useProjectsQuery(filter, userReady)
  const foldersQuery = useProjectFoldersQuery(userReady)
  const archivedQuery = useArchivedProjectsQuery(userReady && archivedRequested)

  const archiveMutation = useArchiveProjectMutation()
  const unarchiveMutation = useUnarchiveProjectMutation()

  const showArchivedProjects = (): void => {
    if (archivedRequested) {
      void archivedQuery.refetch()
    } else {
      setArchivedRequested(true)
    }
  }

  const handleArchive = async (id: string, owner: string): Promise<void> => {
    // Errors are notified in the mutation's onError; swallow the rejection so
    // awaiting callers just resume.
    await archiveMutation.mutateAsync({ id, owner }).catch(() => undefined)
  }

  const handleUnarchive = async (id: string, owner: string): Promise<void> => {
    await unarchiveMutation.mutateAsync({ id, owner }).catch(() => undefined)
  }

  return (
    <div id="home" className="flex flex-col">
      <ProjectDisplay
        filter={filter}
        setFilter={setFilter}
        isLoading={projectsQuery.isFetching}
        isLoadingArchived={archivedQuery.isFetching}
        getProjects={() => void projectsQuery.refetch()}
        getArchivedProjects={showArchivedProjects}
        projects={{ result: projectsQuery.data ?? [] }}
        archivedProjects={{ result: archivedQuery.data ?? [] }}
        folders={foldersQuery.data ?? []}
        handleArchive={handleArchive}
        handleUnarchive={handleUnarchive}
      />
    </div>
  )
}

export default HomeContainer
